import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaEdit, FaTrash, FaCalendarAlt, FaUser, FaTag, FaShoppingCart, FaTimes } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { fetchPromoCodeDetails, deletePromoCode, clearError, clearSuccess } from '../../../redux/Slices/AdminPromoCodesSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';


const AdminPromoCodeDetails = () => {
  
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { promoCode, loading, error, success } = useSelector(state => state.adminPromoCodes);

  const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null, data: null });

  useEffect(() => {
    setTitle('تفاصيل البرومو كود');
    dispatch(clearError());
    dispatch(clearSuccess());
    
    if (id) {
      dispatch(fetchPromoCodeDetails(id));
    }
  }, [setTitle, dispatch, id]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearSuccess());
      if (success.includes('حذف')) {
        navigate('/admin-promo-codes');
      }
    }
    if (error) {
      showErrorToast(error);
      dispatch(clearError());
    }
  }, [success, error, dispatch, navigate]);

  const handleEdit = () => {
    navigate(`/admin-promo-codes/${id}/edit`);
  };

  const handleDelete = () => {
    setConfirmModal({
      isOpen: true,
      action: 'delete',
      data: id,
      title: 'حذف البرومو كود',
      message: `هل أنت متأكد من حذف البرومو كود "${promoCode?.code}"؟`,
      confirmText: 'حذف',
      confirmButtonColor: '#dc3545'
    });
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === 'delete') {
      dispatch(deletePromoCode(confirmModal.data));
      setConfirmModal({ isOpen: false, action: null, data: null });
    }
  };

  const getStatusBadge = (status) => {
    let statusInfo;
    if (typeof status === 'boolean') {
      statusInfo = status 
        ? { text: 'نشط', class: 'bg-success' }
        : { text: 'غير نشط', class: 'bg-danger' };
    } else {
      const statusMap = {
        1: { text: 'نشط', class: 'bg-success' },
        0: { text: 'غير نشط', class: 'bg-danger' },
        true: { text: 'نشط', class: 'bg-success' },
        false: { text: 'غير نشط', class: 'bg-danger' }
      };
      statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    }
    
    return (
      <span className={`badge ${statusInfo.class} text-white fs-6`}>
        {statusInfo.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-EG', {
      style: 'currency',
      currency: 'EGP'
    }).format(amount);
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isActive = (startsAt, expiresAt, status) => {
    if (status !== 1) return false;
    const now = new Date();
    const start = new Date(startsAt);
    const end = new Date(expiresAt);
    return now >= start && now <= end;
  };

  if (loading) {
    return (
      <div className="main_content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
                <p className="mt-3">جاري تحميل تفاصيل البرومو كود...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!promoCode) {
    return (
      <div className="main_content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="alert alert-warning">
                  <h4>البرومو كود غير موجود</h4>
                  <p>لم يتم العثور على البرومو كود المطلوب.</p>
                  <button
                    className="btn"
                    onClick={() => navigate('/admin-promo-codes')}
                    style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
                  >
                    العودة للقائمة
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main_content">
      
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box">
              <div className="page-title-right my-3">
                <button
                  className="btn btn-outline-secondary mx-2"
                  onClick={() => navigate('/admin-promo-codes')}
                >
                  <FaArrowLeft className="mx-2" />
                  العودة
                </button>
                <button
                  className="btn mx-2"
                  onClick={handleEdit}
                  style={{ backgroundColor: '#17a2b8', color: 'white', border: 'none' }}
                >
                  <FaEdit className="mx-2" />
                  تعديل
                </button>
                <button
                  className="btn"
                  onClick={handleDelete}
                  style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}
                >
                  <FaTrash className="mx-2" />
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <motion.div
              className="card shadow-lg border-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{ borderRadius: '20px', overflow: 'hidden' }}
            >
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-md-8">
                    <div className="row g-4">
                      {/* Basic Information */}
                      <div className="col-12">
                        <div className="bg-gradient-primary p-4 rounded-3 mb-4" style={{ background: 'linear-gradient(135deg, #edc80e 0%, #764ba2 100%)' }}>
                          <h5 className="card-title mb-0 text-white">
                            <FaTag className="mx-2" />
                            المعلومات الأساسية
                          </h5>
                        </div>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>كود البرومو:</label>
                            <div className="p-3 bg-light rounded-3 border">
                              <code className="fs-6 text-primary fw-bold">{promoCode.code}</code>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>الحالة:</label>
                            <div className="p-3">
                              {getStatusBadge(promoCode.status)}
                              {isExpired(promoCode.expires_at) && (
                                <span className="badge bg-warning text-dark ms-2">
                                  منتهي الصلاحية
                                </span>
                              )}
                              {isActive(promoCode.starts_at, promoCode.expires_at, promoCode.status) && (
                                <span className="badge bg-success ms-2">
                                  نشط حالياً
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Discount Information */}
                      <div className="col-12">
                        <div className="bg-gradient-success p-4 rounded-3 mb-4" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                          <h5 className="card-title mb-0 text-white">
                            <FaShoppingCart className="mx-2" />
                            معلومات الخصم
                          </h5>
                        </div>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">نسبة الخصم:</label>
                            <div className="p-3 bg-light rounded-3 border">
                              <span className="fs-6 text-success fw-bold">
                                {Math.round(promoCode.discount_percent)}%
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">الحد الأدنى للطلب:</label>
                            <div className="p-3 bg-light rounded-3 border">
                              <span className="fs-6 text-info fw-bold">
                                {formatCurrency(promoCode.min_order)}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">الحد الأقصى للطلب:</label>
                            <div className="p-3 bg-light rounded-3 border">
                              <span className="fs-6 text-info fw-bold">
                                {promoCode.max_order ? formatCurrency(promoCode.max_order) : 'غير محدد'}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">حد الاستخدام:</label>
                            <div className="p-3 bg-light rounded-3 border">
                              <span className="fs-6">
                                {promoCode.usage_limit || 'غير محدود'}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold text-dark">الاستخدام الحالي:</label>
                            <div className="p-3 bg-light rounded-3 border">
                              <span className="fs-6">
                                {promoCode.used_count || 0}
                                {promoCode.usage_limit && (
                                  <span className="text-muted">
                                    / {promoCode.usage_limit}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Date Information */}
                      <div className="col-12">
                        <h5 className="card-title mb-3">
                          <FaCalendarAlt className="mx-2 text-info" />
                          التواريخ
                        </h5>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold">تاريخ البداية:</label>
                            <div className="p-2 bg-light rounded">
                              <span className="fs-6">
                                {formatDate(promoCode.starts_at)}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold">تاريخ الانتهاء:</label>
                            <div className="p-2 bg-light rounded">
                              <span className="fs-6">
                                {formatDate(promoCode.expires_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Creator Information */}
                      <div className="col-12">
                        <h5 className="card-title mb-3">
                          <FaUser className="mx-2 text-secondary" />
                          معلومات المنشئ
                        </h5>
                         <div className="row g-3">
                           <div className="col-md-6">
                             <label className="form-label fw-bold">نوع المنشئ:</label>
                             <div className="p-2 bg-light rounded">
                               <span className="badge bg-secondary text-white fs-6">
                                 {promoCode.created_by_type || 'غير محدد'}
                               </span>
                             </div>
                           </div>
                         </div>
                      </div>
                    </div>
                  </div>

                  {/* Side Info */}
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <div className="mb-3">
                          <FaTag className="text-primary" style={{ fontSize: '3rem' }} />
                        </div>
                        <h4 className="text-primary mb-2">{promoCode.code}</h4>
                        <p className="text-muted mb-3">كود الخصم</p>
                        
                        <div className="mb-3">
                          <div className="fs-3 text-success fw-bold">
                            {promoCode.discount_percent}%
                          </div>
                          <div className="text-muted">خصم</div>
                        </div>

                        <div className="mb-3">
                          <div className="fs-6 text-info fw-bold">
                            {formatCurrency(promoCode.min_order)}
                          </div>
                          <div className="text-muted">الحد الأدنى للطلب</div>
                        </div>

                        <div className="mt-4">
                          {getStatusBadge(promoCode.status)}
                        </div>
                        
                        <div className="mt-2">
                          <span className="badge bg-secondary text-white">
                            {promoCode.created_by_type || 'غير محدد'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, data: null })}
        onConfirm={handleConfirmAction}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmButtonColor={confirmModal.confirmButtonColor}
      />
    </div>
  );
};

export default AdminPromoCodeDetails;
